/**
 * Built-in fonts (all SIL Open Font License, self-hosted via @fontsource).
 * The renderer ships the font files (packages/renderer/scripts/generate-fonts.mjs),
 * so the editor, thumbnails and print PDFs use exactly the same glyphs.
 */

export type FontCategory = "custom" | "sans" | "serif" | "display" | "handwriting";

export interface FontFamily {
  family: string;
  /** npm package below @fontsource/ (built-in fonts only) */
  package?: string;
  category: FontCategory;
  weights: number[];
  /** Weights that also exist in italic. */
  italics: number[];
}

const all = [100, 200, 300, 400, 500, 600, 700, 800, 900];

export const FONT_FAMILIES: readonly FontFamily[] = [
  { family: "Inter", package: "inter", category: "sans", weights: all, italics: all },
  { family: "Montserrat", package: "montserrat", category: "sans", weights: all, italics: all },
  { family: "Poppins", package: "poppins", category: "sans", weights: all, italics: all },
  { family: "Roboto", package: "roboto", category: "sans", weights: all, italics: all },
  {
    family: "Open Sans",
    package: "open-sans",
    category: "sans",
    weights: [300, 400, 500, 600, 700, 800],
    italics: [300, 400, 500, 600, 700, 800],
  },
  {
    family: "Lato",
    package: "lato",
    category: "sans",
    weights: [100, 300, 400, 700, 900],
    italics: [100, 300, 400, 700, 900],
  },
  { family: "DM Sans", package: "dm-sans", category: "sans", weights: all, italics: all },
  { family: "Raleway", package: "raleway", category: "sans", weights: all, italics: all },
  {
    family: "Nunito",
    package: "nunito",
    category: "sans",
    weights: [200, 300, 400, 500, 600, 700, 800, 900],
    italics: [200, 300, 400, 500, 600, 700, 800, 900],
  },
  {
    family: "Space Grotesk",
    package: "space-grotesk",
    category: "sans",
    weights: [300, 400, 500, 600, 700],
    italics: [],
  },
  {
    family: "Playfair Display",
    package: "playfair-display",
    category: "serif",
    weights: [400, 500, 600, 700, 800, 900],
    italics: [400, 500, 600, 700, 800, 900],
  },
  {
    family: "Merriweather",
    package: "merriweather",
    category: "serif",
    weights: [300, 400, 500, 600, 700, 800, 900],
    italics: [300, 400, 500, 600, 700, 800, 900],
  },
  {
    family: "Lora",
    package: "lora",
    category: "serif",
    weights: [400, 500, 600, 700],
    italics: [400, 500, 600, 700],
  },
  {
    family: "Source Serif 4",
    package: "source-serif-4",
    category: "serif",
    weights: [200, 300, 400, 500, 600, 700, 800, 900],
    italics: [200, 300, 400, 500, 600, 700, 800, 900],
  },
  {
    family: "Oswald",
    package: "oswald",
    category: "display",
    weights: [200, 300, 400, 500, 600, 700],
    italics: [],
  },
  { family: "Bebas Neue", package: "bebas-neue", category: "display", weights: [400], italics: [] },
  {
    family: "Archivo Black",
    package: "archivo-black",
    category: "display",
    weights: [400],
    italics: [],
  },
  {
    family: "Caveat",
    package: "caveat",
    category: "handwriting",
    weights: [400, 500, 600, 700],
    italics: [],
  },
  { family: "Pacifico", package: "pacifico", category: "handwriting", weights: [400], italics: [] },
];

export const FONT_CATEGORY_LABELS: Record<FontCategory, string> = {
  custom: "Schriften der Organisation",
  sans: "Serifenlos",
  serif: "Serif",
  display: "Display",
  handwriting: "Handschrift",
};

export const WEIGHT_LABELS: Record<number, string> = {
  100: "Thin",
  200: "Extra Light",
  300: "Light",
  400: "Regular",
  500: "Medium",
  600: "Semibold",
  700: "Bold",
  800: "Extra Bold",
  900: "Black",
};

/**
 * Fonts of the current organization (uploads, imported Google Fonts). The web
 * app registers them after loading; the backend passes them explicitly where
 * needed (it serves many organizations at once).
 */
let customFonts: readonly FontFamily[] = [];

export function setCustomFonts(fonts: readonly FontFamily[]): void {
  customFonts = fonts;
}

export function getCustomFonts(): readonly FontFamily[] {
  return customFonts;
}

export function isBuiltInFont(family: string): boolean {
  return FONT_FAMILIES.some((font) => font.family.toLowerCase() === family.toLowerCase());
}

export function getFontFamily(family: string): FontFamily | undefined {
  return (
    FONT_FAMILIES.find((font) => font.family === family) ??
    customFonts.find((font) => font.family === family)
  );
}

/** Built-in or registered font; `extra` adds families known to the caller (backend). */
export function isKnownFont(family: string, extra: readonly string[] = []): boolean {
  return getFontFamily(family) !== undefined || extra.includes(family);
}

/** Closest available weight of a family (browsers would otherwise fake bold). */
export function closestWeight(family: string, weight: number): number {
  const font = getFontFamily(family);
  if (!font) return weight;
  return font.weights.reduce((best, candidate) =>
    Math.abs(candidate - weight) < Math.abs(best - weight) ? candidate : best,
  );
}

export function hasItalic(family: string, weight: number): boolean {
  return getFontFamily(family)?.italics.includes(weight) ?? false;
}
